import { API_ENDPOINTS } from "../config/constants";

const SYNC_INTERVAL_MS = 30_000;
let syncInFlight = false;
let syncTimer = null;

const isDesktop = () =>
  typeof window !== "undefined" && Boolean(window.desktopOrderQueue);

const newId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `desktop-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const publishQueueChange = (detail = {}) => {
  window.dispatchEvent(new CustomEvent("desktop-order-sync-change", { detail }));
};

export const enqueuePendingOrder = async ({ restaurantId, payload }) => {
  if (!isDesktop()) return null;
  const id = newId();
  await window.desktopOrderQueue.enqueue({
    id,
    restaurantId,
    endpoint: API_ENDPOINTS.CREATE_ORDER,
    payload,
  });
  publishQueueChange({ type: "queued", id, restaurantId });
  return id;
};

export const getPendingOrderCount = async (restaurantId) => {
  if (!isDesktop()) return 0;
  const rows = await window.desktopOrderQueue.list({ restaurantId });
  return Array.isArray(rows) ? rows.length : 0;
};

const isPermanentServerFailure = (response) => response.status >= 400 && response.status < 500;

export const syncPendingOrders = async () => {
  if (!isDesktop() || !navigator.onLine || syncInFlight) return { synced: 0, pending: 0 };
  syncInFlight = true;
  let synced = 0;
  try {
    const rows = await window.desktopOrderQueue.list({});
    for (const item of rows || []) {
      try {
        const response = await fetch(item.endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            // Backend should persist this key to guarantee exactly-once retries.
            "X-Idempotency-Key": item.id,
          },
          body: JSON.stringify(item.payload),
        });
        const result = await response.json().catch(() => ({}));
        if (response.ok && result?.success !== false) {
          await window.desktopOrderQueue.remove({ id: item.id });
          synced += 1;
          publishQueueChange({ type: "synced", id: item.id, restaurantId: item.restaurantId });
          continue;
        }
        await window.desktopOrderQueue.markFailure({
          id: item.id,
          error: result?.message || `Server returned ${response.status}`,
          permanent: isPermanentServerFailure(response),
        });
      } catch (error) {
        await window.desktopOrderQueue.markFailure({
          id: item.id,
          error: error?.message || "Network request failed",
          permanent: false,
        });
        break; // Connection is likely unavailable; retry later rather than hammering it.
      }
    }
    const pending = await getPendingOrderCount();
    return { synced, pending };
  } finally {
    syncInFlight = false;
  }
};

export const startOrderSyncService = () => {
  if (!isDesktop() || syncTimer) return;
  const run = () => syncPendingOrders().catch(() => {});
  window.addEventListener("online", run);
  syncTimer = window.setInterval(run, SYNC_INTERVAL_MS);
  run();
};
